import { AxiosResponse } from 'axios'
import {
  AsteriskChannel,
  JSONApiListResponse,
  JSONApiResponse,
} from '../api.js'
import { BaseAPI, JSONApiError } from './BaseAPI.js'
import {
  User,
  LoginResponse,
  LoginTypes,
  LoginAdditionalOptions,
  UserEvents,
  ApiToken,
  AuthToken,
} from '../types/User.js'

export default class UsersAPI extends BaseAPI<User, UserEvents> {
  override get resource(): string {
    return '/users'
  }

  override get hasEvents(): boolean {
    return true
  }

  public async activeChannel(id: number | string): Promise<AsteriskChannel> {
    const result = await this._client.get(`users/${id}/call`)

    return result.data.data
  }

  public async renew(id: number | string): Promise<void> {
    await this._client.patch(`users/${id}/renew`)
  }

  public async lockLead(id: number | string): Promise<void> {
    await this._client.post(`users/me/campaignListRecord/${id}`)
  }

  public async unlockLead(id: number | string): Promise<void> {
    await this._client.delete(`users/me/campaignListRecord/${id}`)
  }

  public async lockUniqueIdentifier(uniqueId: string | number): Promise<void> {
    await this._client.post(`users/me/uniqueIdentifier/${uniqueId}`)
  }

  public async unlockUniqueIdentifier(
    uniqueId: string | number,
  ): Promise<void> {
    await this._client.delete(`users/me/uniqueIdentifier/${uniqueId}`)
  }

  public async logout(): Promise<void> {
    await this._client.delete(`${this.resource}/sessions`)
  }

  async tokens(): Promise<JSONApiListResponse<ApiToken>> {
    const result: AxiosResponse = await this._client.get(
      `${this.resource}/tokens`,
    )
    return result.data
  }

  async newToken(item: ApiToken): Promise<JSONApiResponse<AuthToken>> {
    const result: AxiosResponse = await this._client.post(
      `${this.resource}/tokens`,
      { data: item },
    )
    return result.data
  }

  async deleteToken(id: string | number): Promise<boolean> {
    await this._client.delete(`${this.resource}/tokens/${id}`)
    return true
  }

  public async login(
    username: string,
    password: string,
    { type, requestExtension }: LoginAdditionalOptions = {},
  ): Promise<LoginResponse> {
    if (!type) {
      type = LoginTypes.login
    }
    if (!requestExtension) {
      requestExtension = false
    }
    try {
      const result = await this._client.post(`${this.resource}/sessions`, {
        data: { type, attributes: { username, password, requestExtension } },
      })
      return result.data?.data?.attributes
    } catch (err: any) {
      if (err.response) {
        if (err.response.data?.errors) {
          throw new JSONApiError(err.response.data)
        } else {
          throw err
        }
      } else {
        throw err
      }
    }
  }
}
